import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { registerIncidentLifecycleTourApi } from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourBridge';
import {
  INCIDENT_TOUR_CONTINUE_KEY,
  incidentTourPlanningHref,
} from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourSteps';
import { registerFocTransitionTourApi } from 'components/MapPage/focTransitionTour/focTransitionTourBridge';
import {
  FOC_TRANSITION_TOUR_CONTINUE_KEY,
  focTransitionPlanningHref,
} from 'components/MapPage/focTransitionTour/focTransitionTourSteps';
import { FaArrowUp } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { useEscalateIncident } from 'hooks/incident/useIncidentManagement';
import {
  useAddIncidentCalendarEvent,
  eventTypes,
} from 'components/IAP/hooks/incidentCalendarHooks';
import { v7 } from 'uuid';
import { useIcs201Op0 } from 'hooks/incident/useIcs201Op0';
import { useIncident } from 'components/IAP/hooks/incidentHooks';

export const EscalateResponseButton = () => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const { data: incident } = useIncident();
  const escalateIncident = useEscalateIncident();
  const mutation = useAddIncidentCalendarEvent();
  const { hasExported, exportIcs201Op0 } = useIcs201Op0(incident?.id);

  const createCalendarEvents = async () => {
    if (!incident) return;
    const events = eventTypes.filter((type) => type.title !== 'Other');
    for (let i = 0; i < events.length; i++) {
      mutation.mutateAsync({
        id: v7(),
        incidentId: incident.id,
        title: events[i].title,
        purpose: events[i].agenda,
        color: events[i].code,
        start: new Date(new Date().getTime() + i * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + (i + 2) * 60 * 60 * 1000),
        location: 'TBD',
        externalAttendees: '',
        eventTypeLabel: events[i].title,
      });
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const escalateThisWorkspace = async (
    continueTour: 'lifecycle' | 'focTransition' | false
  ): Promise<string | null> => {
    if (!incident) return null;
    if (continueTour === 'lifecycle') {
      sessionStorage.setItem(INCIDENT_TOUR_CONTINUE_KEY, 'planning');
    }
    if (continueTour === 'focTransition') {
      sessionStorage.setItem(FOC_TRANSITION_TOUR_CONTINUE_KEY, 'planning');
    }
    if (!hasExported) {
      exportIcs201Op0(false);
    }
    try {
      await escalateIncident.mutateAsync({ incidentId: incident.id });
      await createCalendarEvents();
      if (continueTour === 'focTransition') {
        return focTransitionPlanningHref(incident.id);
      }
      return incidentTourPlanningHref(incident.id);
    } catch {
      if (continueTour === 'lifecycle') {
        sessionStorage.removeItem(INCIDENT_TOUR_CONTINUE_KEY);
      }
      if (continueTour === 'focTransition') {
        sessionStorage.removeItem(FOC_TRANSITION_TOUR_CONTINUE_KEY);
      }
      return null;
    }
  };

  useEffect(
    () =>
      registerIncidentLifecycleTourApi({
        escalateCurrentWorkspace: () => escalateThisWorkspace('lifecycle'),
      }),
    // Recreate when the incident or export state the escalate path closes over changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incident?.id, hasExported]
  );

  useEffect(
    () =>
      registerFocTransitionTourApi({
        openEscalateModal: () => setShow(true),
        escalateWorkspace: () => escalateThisWorkspace('focTransition'),
        navigateTo: (href) => navigate(href),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incident?.id, hasExported, navigate]
  );

  const handleConfirm = () => {
    if (!incident) return;

    void (async () => {
      const href = await escalateThisWorkspace(false);
      if (!href) return;
      setShow(false);
      navigate(
        `/iap/${incident.id}/dashboard` +
          '?' +
          new URLSearchParams({
            mode: 'future',
            formsView: 'working',
            phase: '1',
          }).toString()
      );
    })();
  };

  if (!incident) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="dark tw:me-1 tw:gap-1"
        data-tour="incident-escalate"
        onClick={() => setShow(true)}
      >
        <SharedIcon iconName="arrow_upward" size="sm" />
        Escalate Response
      </Button>
      <CustomModal
        show={show}
        heading="Escalate Response"
        onClose={handleClose}
        onConfirm={handleConfirm}
        saveButton="Escalate"
        icon={<FaArrowUp className="text-primary" size={20} />}
      >
        <div data-tour="incident-escalate-modal">
          <p className="text-weak mb-5">
            Select a planning workflow to escalate the incident response level
          </p>
          <div data-tour="incident-escalate-workflow">
            <PratusSelect
              label="Planning Workflow"
              placeholder="Select a planning workflow"
              options={[
                {
                  value: 'incident_command_system',
                  label: 'Incident Command System',
                },
              ]}
              // Planning Workflow is not used anywhere right now.
              // It is strictly for sales to demo that new planning workflows can be created if needed by the customer.
              // onChange={() => {}}
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};
